/**
 * Projects API Schemas
 *
 * Pure Zod schemas for API request/response validation.
 * DB types come from @aica/database repositories.
 *
 * SINGLE SOURCE OF TRUTH for API contract.
 */

import { z } from 'zod/v4';
import { storedVendorNameSchema } from './common';

// ============================================================================
// Enums
// ============================================================================

/**
 * Project Status
 *
 * Lifecycle state of a project:
 * - active: Project is operational and accepting new records
 * - paused: Project is temporarily halted — every direct record-intake route
 *   (Public API, session UI upload) rejects new records with
 *   `project_not_accepting_records` / 409 (see
 *   `apps/edge-api/src/utils/project-status.ts`). Still queryable in
 *   filters, dashboards and analytics — only intake stops, nothing about the
 *   project's existing data changes.
 * - archived: Project is read-only, no new records accepted
 *
 * Archived projects remain queryable for historical analysis
 * but are hidden from most UI dropdowns and default filters.
 */
export const projectStatusSchema = z.enum(['active', 'paused', 'archived']);

/**
 * Project Role Hierarchy
 *
 * User permissions within a specific project:
 * - admin: Full control (edit settings, manage users, delete project)
 * - member: Can create/view records, run analyses, add annotations
 * - viewer: Read-only access to records and analyses
 *
 * NOTE: Project roles are separate from organization roles.
 * A user can be org 'viewer' but project 'admin' for specific projects.
 * Organization-level admins have implicit admin role on all projects.
 */
export const projectRoleSchema = z.enum(['admin', 'member', 'viewer']);

/**
 * Project roles that GRANT ACCESS — the one list the server's membership scope
 * (`hasAccessProjectRole`, `middleware/auth.ts` building `auth.projectRoles`,
 * the digests) and the UI's "does this user have any projects" read both use.
 * `project_users.role` is unconstrained, so a row with any other value is not
 * membership. Not the same question as {@link projectRoleSchema} (which roles
 * may be ASSIGNED) even while the two lists happen to be equal: each changes
 * for its own reason, so each is spelled out.
 */
export const ACCESS_PROJECT_ROLES = ['admin', 'member', 'viewer'] as const;

/** True when a stored `project_users.role` value grants project access. */
export function isAccessProjectRole(role: unknown): boolean {
  return (ACCESS_PROJECT_ROLES as readonly unknown[]).includes(role);
}

/**
 * Channel Type
 *
 * Communication channel for records in this project:
 * - call: Voice calls (requires ASR for transcription)
 * - chat: Text-based chat/messaging (already text, no ASR needed)
 *
 * Channel type determines:
 * - Whether ASR (transcription) is required
 * - Which integrations are relevant (e.g., ElevenLabs for calls)
 * - UI display and filtering options
 */
export const channelTypeSchema = z.enum(['call', 'chat']);

/**
 * Integration Type Categories
 *
 * Third-party service integrations for project functionality:
 * - asr: Automatic Speech Recognition (transcription providers)
 *   Examples: ElevenLabs Scribe v2
 * - llm: Large Language Models (analysis providers)
 *   Examples: OpenAI GPT-5-mini for quality scoring
 * - crm: Customer Relationship Management (data source/sink)
 *   Examples: Bitrix24 for contact sync and lead updates
 * - email: Email delivery (notification sending)
 *   Examples: Resend for transactional emails
 * - storage: Cloud storage (audio file storage)
 *   Examples: Cloudflare R2 for audio uploads
 * - webhook: HTTP callbacks (event notifications)
 *   Examples: n8n for custom workflow automation
 *
 * Multiple integrations of the same type can exist per project
 * (e.g., ElevenLabs for ASR).
 */
export const integrationTypeSchema = z.enum([
  'asr',
  'llm',
  'crm',
  'email',
  'storage',
  'webhook',
]);

/**
 * Integration Vendor Registry
 *
 * Supported third-party providers for integrations:
 * - elevenlabs: ASR provider (Scribe v2 model, multi-language, speaker diarization)
 * - openai: LLM provider (GPT-5-mini, GPT-4o)
 * - bitrix24: CRM platform (contact sync, deal updates, embedded app)
 * - resend: Email delivery service (transactional emails)
 * - r2: Cloudflare R2 storage (audio file uploads)
 * - n8n: Workflow automation platform (webhook endpoints)
 *
 * Each vendor maps to one or more integration types.
 * New vendors require code changes to add provider implementations.
 */
export const integrationVendorSchema = z.enum([
  'elevenlabs',
  'openai',
  'bitrix24',
  'resend',
  'r2',
  'n8n',
]);

// ============================================================================
// List Projects Schemas
// ============================================================================

export const listProjectsQuerySchema = z.object({
  status: projectStatusSchema.optional(),
  search: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

// ============================================================================
// Create/Update Project Schemas
// ============================================================================

export const createProjectRequestSchema = z
  .object({
    name: z.string().min(1).max(255),
    description: z.string().max(1000).optional(),
    settings: z.record(z.string(), z.unknown()).optional(),
    promptProfileId: z.string().uuid().optional(),
  })
  .strict();

export const updateProjectRequestSchema = z
  .object({
    name: z.string().min(1).max(255).optional(),
    description: z.string().max(1000).optional(),
    status: projectStatusSchema.optional(),
    settings: z.record(z.string(), z.unknown()).optional(),
    /** null clears the project's promptProfileId (falls back to org-level resolution). */
    promptProfileId: z.string().uuid().nullable().optional(),
    /** Pin the org's bound prompt profile — skip category-based auto-selection. */
    promptProfilePinned: z.boolean().optional(),
    /**
     * @deprecated Accepted and IGNORED since #707. Removing it outright from
     * this `.strict()` schema would have broken saves for real users: prod
     * deploys edge-api BEFORE ui-worker, and every already-loaded copy of the
     * old settings screen puts `scriptPinned` in its PATCH body — those
     * requests would fail validation before reaching the handler, during the
     * deploy window and afterwards in any long-lived tab. Delete once old
     * clients have drained, together with the `projects.script_pinned` column.
     */
    scriptPinned: z.boolean().optional(),
  })
  .strict();

// ============================================================================
// Project Response Schemas
// ============================================================================

/**
 * Project Detail Response
 *
 * Complete project information from GET /api/v1/projects/:id.
 *
 * Key fields:
 * - settingsJson: JSON string containing project configuration (ASR/LLM models,
 *   provider preferences, alert rules, etc.). Stored as string in DB,
 *   parsed by backend when needed. Structure defined in ProjectSettings schema.
 * - isEmbedded: True if project was created via Bitrix24 embedded app.
 *   Embedded projects have special handling for CRM sync and authentication.
 * - externalCrmId: Reference ID in external CRM (e.g., Bitrix24 deal/lead ID).
 *   Used for bi-directional sync and deep linking back to CRM.
 * - promptProfileId: Reference to custom prompt template for analysis.
 *   If null, uses organization default prompts.
 * - promptProfilePinned: opts the project out of the org's category-bound
 *   auto-selection (migration 059) in favor of its own promptProfileId.
 *   There is no script counterpart: a project's own script already outranks
 *   the org's category binding whether pinned or not (#702), so the pin
 *   decided nothing in all four combinations and was removed (#707).
 * - role: Current user's role on this specific project (admin/member/viewer).
 *   May differ from organization-level role.
 * - recordCount: Cached count of records in project for dashboard display.
 */
export const projectResponseSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  status: projectStatusSchema,
  settingsJson: z.string().nullable(), // JSON string from DB (not parsed object)
  isEmbedded: z.boolean(),
  externalCrmId: z.string().nullable(),
  promptProfileId: z.string().nullable(),
  /** If true, the project uses its own promptProfileId instead of the org's category-bound profile. */
  promptProfilePinned: z.boolean().default(false),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
  role: z.string().nullable().optional(), // User's role on this project
  recordCount: z.number().int().optional(), // Count of records in project
  agentsCount: z.number().int().optional(), // Count of agents assigned to project
  /**
   * Rolling-window average quality score, same computation the list returns.
   *
   * `GET /projects/:id` has been sending `avgScore` and `channels` alongside
   * `recordCount` since they were added, but they were never declared here —
   * and Zod strips unknown keys, so `getProjectById()` handed back `undefined`
   * for both while `ProjectWithStats` typed them as present. Declared as
   * optional to match `projectListItemSchema`, which already carries them.
   */
  avgScore: z.number().nullable().optional(),
  /** Resolved processing channels ('call' / 'chat'), same as the list. */
  channels: z.array(z.string()).optional(),
});

export const projectListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  status: projectStatusSchema,
  isEmbedded: z.boolean(),
  recordCount: z.number().int().optional(),
  agentsCount: z.number().int().optional(),
  avgScore: z.number().nullable().optional(),
  channels: z.array(z.string()).optional(),
  createdAt: z.string(),
  promptProfileId: z.string().nullable().optional(),
  promptProfileName: z.string().nullable().optional(),
  /**
   * Name of the project's default script, resolved server-side
   * (`routes/projects/core.ts` scriptNameMap). Declared here because the list
   * response is parsed with this schema before the mapper sees it: an
   * undeclared field is STRIPPED, not passed through, so the project cards
   * showed no script name however well the backend enriched the row.
   */
  scriptName: z.string().nullable().optional(),
  settingsJson: z.string().nullable().optional(),
  /**
   * The caller's `project_users` role on this project. The route already sends
   * it for non-org-wide callers; declared here because the list response is
   * parsed with this schema and an undeclared field is stripped (#1159).
   */
  role: z.string().nullable().optional(),
});

export const listProjectsResponseSchema = z.object({
  projects: z.array(projectListItemSchema),
  total: z.number().int(),
  limit: z.number().int().optional(),
  offset: z.number().int().optional(),
});

export const getProjectResponseSchema = z.object({
  project: projectResponseSchema,
});

export const createProjectResponseSchema = z.object({
  project: projectResponseSchema,
  message: z.string().optional(),
});

// ============================================================================
// Channel Schemas
// ============================================================================

/**
 * Create Channel Request
 *
 * Channels organize records by communication medium within a project.
 * Example use cases:
 * - "Sales Calls" channel for voice calls
 * - "Support Chat" channel for live chat transcripts
 * - "Inbound" vs "Outbound" channels for filtering
 *
 * Fields:
 * - externalId: Reference ID in external system (e.g., Bitrix24 channel ID)
 * - config: Channel-specific settings (custom fields, routing rules, etc.)
 *   Structure is flexible and defined by channel type requirements.
 */
export const createChannelRequestSchema = z
  .object({
    name: z.string().min(1).max(255),
    type: channelTypeSchema,
    externalId: z.string().optional(),
    config: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();

export const updateChannelRequestSchema = z
  .object({
    name: z.string().min(1).max(255).optional(),
    type: channelTypeSchema.optional(),
    externalId: z.string().optional(),
    config: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();

export const channelResponseSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  name: z.string(),
  type: channelTypeSchema,
  externalId: z.string().nullable(),
  config: z.unknown().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

export const listChannelsResponseSchema = z.object({
  channels: z.array(channelResponseSchema),
  total: z.number().int(),
});

// ============================================================================
// Integration Schemas
// ============================================================================

/**
 * Create Integration Request
 *
 * Connect third-party services to a project for processing records.
 *
 * Fields:
 * - type: Integration category (asr, llm, crm, email, storage, webhook)
 * - vendor: Specific provider (elevenlabs, openai, bitrix24, etc.)
 * - credsRef: Reference to encrypted credentials in organization_provider_credentials.
 *   If omitted, uses organization-level default credentials.
 *   Format: "org:{orgId}:provider:{vendor}" or custom reference.
 * - config: Integration-specific configuration (model names, API endpoints, etc.)
 *   Structure varies by vendor. Examples:
 *   - ASR: { model: "scribe_v2", language: "en", diarization: true }
 *   - LLM: { model: "gpt-5.6-luna", temperature: 0.2, maxTokens: 4000 }
 *   - Webhook: { url: "https://...", events: ["record.completed"] }
 * - enabled: If false, integration exists but is not used in pipeline.
 *   Useful for temporarily disabling without deleting configuration.
 *
 * Multiple integrations of the same type are supported (e.g., primary + fallback ASR).
 */
export const createIntegrationRequestSchema = z
  .object({
    type: integrationTypeSchema,
    vendor: integrationVendorSchema,
    credsRef: z.string().optional(),
    config: z.record(z.string(), z.unknown()).optional(),
    enabled: z.boolean().default(true),
  })
  .strict();

export const updateIntegrationRequestSchema = z
  .object({
    credsRef: z.string().optional(),
    config: z.record(z.string(), z.unknown()).optional(),
    enabled: z.boolean().optional(),
  })
  .strict();

// Same rule as `storedProviderNameSchema`, same reason: a row naming a vendor
// we have since retired ('openrouter', removed 2026-08-11) must not void the
// whole list response. Generic — retiring the next vendor needs no edit here;
// requests keep the strict `integrationVendorSchema`.
export const storedIntegrationVendorSchema = storedVendorNameSchema<IntegrationVendor>();

export const integrationResponseSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  type: integrationTypeSchema,
  vendor: storedIntegrationVendorSchema,
  credsRef: z.string().nullable(),
  config: z.unknown().nullable(),
  enabled: z.boolean(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

export const listIntegrationsResponseSchema = z.object({
  integrations: z.array(integrationResponseSchema),
  total: z.number().int(),
});

// ============================================================================
// Alert Notification Channels Schemas
// ============================================================================

/**
 * WRITE side of the team alert channels — what a PUT is allowed to set.
 *
 * Deliberately stricter than `alertNotificationChannelsSchema` above, which
 * describes what may be ON DISK: the read schema must accept rows written
 * before today's rules (an `http://` webhook, a withdrawn `email` block),
 * while this one governs what may be added now.
 *
 * It lives here, not in the route, so the form that builds the request can be
 * tested against the very schema that will judge it — that guarantee used to
 * rest on reading both files and believing they agreed.
 */
export const alertNotificationChannelsWriteSchema = z.object({
  // No `email` key: the team-channel email was configurable and testable but
  // never delivered — `deliverToTeamChannels` implements Telegram, Bitrix24
  // chat and webhook only (Codex P1). Rather than ship a transport for it, the
  // channel is withdrawn: new writes cannot set it.
  //
  // The READ schema still accepts it (packages/api-types projects.ts and
  // packages/core project-settings.ts), because projects configured before
  // this change have the key on disk and their GET must not start failing.
  // Stored values are simply never delivered to and no longer editable.
  telegram: z
    .object({
      enabled: z.boolean(),
      botToken: z.string().max(100),
      chatId: z.string().max(50),
    })
    .optional(),
  webhook: z
    .object({
      enabled: z.boolean(),
      url: z
        .string()
        .url()
        .max(500)
        .refine((url) => url.startsWith('https://'), {
          message: 'Only HTTPS URLs are allowed for webhooks',
        }),
      secret: z.string().max(256).optional(),
    })
    .optional(),
  inApp: z
    .object({
      enabled: z.boolean(),
    })
    .optional(),
  /**
   * Same shape as the read-side `alertNotificationChannels.bitrix24_chat` in
   * packages/core/src/schemas/project-settings.ts. This field was missing
   * (review finding I3): since the write is a full replace of
   * alertNotificationChannels (not a merge), ANY PUT — even one only
   * touching `email` — silently wiped an existing bitrix24_chat config, and
   * Task 13 is what made that channel load-bearing (a real delivery
   * transport, not just a settings screen).
   */
  bitrix24_chat: z
    .object({
      enabled: z.boolean(),
      chatId: z.string().min(1).max(50).regex(/^\d+$/, 'Chat ID must be numeric'),
    })
    .optional(),
  /**
   * Team-channel severity filter (Task 13). Same shape as the read-side
   * `alertNotificationChannels.teamFilter` in
   * packages/core/src/schemas/project-settings.ts — kept identical rather
   * than merely compatible, so the two schemas can't drift on what a valid
   * value is. Without this field Zod's default "strip unknown keys"
   * behaviour silently dropped it: a PUT that set it returned 200 while the
   * value vanished, which is worse than the field not existing.
   */
  teamFilter: z
    .object({
      minSeverity: z.enum(['low', 'medium', 'high', 'critical']).default('high'),
    })
    .optional(),
});

/**
 * Team alert-delivery channels for a project — email, Telegram, webhook,
 * Bitrix24 chat, in-app, plus the team-wide severity threshold
 * (`teamFilter.minSeverity`, independent of personal subscriptions).
 *
 * This is the READ-side shape: it validates whatever GET
 * .../alert-notification-channels returns, so it must be at least as
 * permissive as what can actually be stored — i.e. it mirrors
 * `alertNotificationChannels` in packages/core/src/schemas/project-settings.ts
 * (the schema `parseProjectSettings` uses to read the column back), not the
 * stricter `AlertNotificationChannelsSchema` the PUT handler in
 * apps/edge-api/src/routes/projects/lifecycle.ts validates new writes with.
 * Concretely: `webhook.url` here has no https-only `.refine()` — the write
 * endpoint has enforced https since it shipped, but the storage schema never
 * did, so a project whose webhook predates that endpoint (or was written
 * before the constraint existed) can have `http://` sitting in D1 today. A
 * stricter read schema would reject that row's GET response outright (review
 * finding I1) — the read side must describe what's on disk, not what new
 * writes are allowed to add.
 *
 * Also deliberately looser than the write schema on `botToken`/`secret`:
 * both are optional here. The read endpoint masks secrets and can omit them
 * entirely — `maskSecret()` returns `undefined` when nothing is configured —
 * while the write endpoint requires the key present (even as an empty
 * string) whenever its parent channel object is sent. That's a form-level
 * discipline the UI must uphold (see the plan's Global Constraints — sending
 * the mask back overwrites the real secret with the mask), not something
 * this schema can enforce: a plain `z.string()` can't tell a mask from a
 * real value.
 */
export const alertNotificationChannelsSchema = z.object({
  email: z
    .object({
      enabled: z.boolean(),
      recipients: z.array(z.string().email().max(255)).max(20),
    })
    .optional(),
  telegram: z
    .object({
      enabled: z.boolean(),
      botToken: z.string().max(100).optional(),
      chatId: z.string().max(50),
    })
    .optional(),
  webhook: z
    .object({
      enabled: z.boolean(),
      // No https-only refine — see the doc comment above this schema.
      url: z.string().url().max(500),
      secret: z.string().max(256).optional(),
    })
    .optional(),
  bitrix24_chat: z
    .object({
      enabled: z.boolean(),
      chatId: z.string().min(1).max(50).regex(/^\d+$/, 'Chat ID must be numeric'),
    })
    .optional(),
  inApp: z
    .object({
      enabled: z.boolean(),
    })
    .optional(),
  /**
   * Independent of personal alert subscriptions AND of the org's personal-
   * delivery threshold (`alertInstantMinSeverity`): a group chat visible to
   * people with no Aelo account must not become a feed of every second
   * conversation, so it gets its own threshold — default `high`.
   */
  teamFilter: z
    .object({
      minSeverity: z.enum(['low', 'medium', 'high', 'critical']).default('high'),
    })
    .optional(),
});

/** GET /api/v1/projects/:id/alert-notification-channels response envelope. */
export const getAlertNotificationChannelsResponseSchema = z.object({
  channels: alertNotificationChannelsSchema,
});

/** POST /api/v1/projects/:id/alert-notification-channels/test response. */
export const testAlertNotificationChannelResponseSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
});

// ============================================================================
// Project Stats Schemas
// ============================================================================

export const projectStatsResponseSchema = z.object({
  stats: z.object({
    totalRecords: z.number().int(),
    completedRecords: z.number().int(),
    failedRecords: z.number().int(),
    pendingRecords: z.number().int(),
    totalDurationMinutes: z.number(),
    avgAnalysisScore: z.number().nullable(),
  }),
});

// ============================================================================
// Quality weights (#874) — importance of the six quality criteria in a project
// ============================================================================

/**
 * One weight: an integer importance 0-10. Integers keep `Σw` exact in all three
 * implementations of the score (JS, SQLite, Postgres) — see
 * docs/plans/2026-10-05-quality-weights.md §4.
 */
const qualityWeightValueSchema = z.number().int().min(0).max(10);

/**
 * Weights of the six quality criteria — the WRITE contract: every current key is
 * required, the sum is positive, nothing else is accepted.
 *
 * The keys are spelled out here on purpose. `@aica/core` depends on this package,
 * not the other way round, so `QUALITY_SUB_METRIC_KEYS` cannot be imported; a test
 * in `@aica/core` pins `Object.keys(qualityWeightsSchema.shape)` to it.
 */
export const qualityWeightsSchema = z
  .object({
    greetingScore: qualityWeightValueSchema,
    needsScore: qualityWeightValueSchema,
    clarityScore: qualityWeightValueSchema,
    objectionScore: qualityWeightValueSchema,
    closingScore: qualityWeightValueSchema,
    politenessScore: qualityWeightValueSchema,
  })
  .strict()
  .refine((weights) => Object.values(weights).some((weight) => weight > 0), {
    message: 'At least one criterion must have a weight greater than zero',
  });

/** The six current keys, in presentation order (pinned to the core list by a test). */
const QUALITY_WEIGHT_KEYS = [
  'greetingScore',
  'needsScore',
  'clarityScore',
  'objectionScore',
  'closingScore',
  'politenessScore',
] as const;

/** The shape a usable stored value takes: the current keys, each optional. */
const storedQualityWeightsObjectSchema = z.object({
  greetingScore: qualityWeightValueSchema.optional(),
  needsScore: qualityWeightValueSchema.optional(),
  clarityScore: qualityWeightValueSchema.optional(),
  objectionScore: qualityWeightValueSchema.optional(),
  closingScore: qualityWeightValueSchema.optional(),
  politenessScore: qualityWeightValueSchema.optional(),
});

/**
 * The usable part of a stored value, or `null` — the same four conditions as
 * `canonicalizeQualityWeights` (`@aica/core`, spec §4), minus its equal-weights
 * folding: (1) a JSON object, (2) at least one current key, (3) every present
 * current key a whole number 0..10 (`3.0` is `3`; `2.5`, `"3"`, `true`, `null`
 * spoil the whole value), (4) a positive sum. Keys the code does not know are
 * left out of the result.
 */
function usableStoredQualityWeights(stored: unknown): Record<string, number> | null {
  if (stored === null || typeof stored !== 'object' || Array.isArray(stored)) return null;
  const record = stored as Record<string, unknown>;
  const usable: Record<string, number> = {};
  let sum = 0;
  for (const key of QUALITY_WEIGHT_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(record, key)) continue;
    const value = record[key];
    if (
      typeof value !== 'number' ||
      !Number.isInteger(value) ||
      value < 0 ||
      value > 10
    ) {
      return null;
    }
    usable[key] = value;
    sum += value;
  }
  return Object.keys(usable).length === 0 || sum === 0 ? null : usable;
}

/**
 * A stored value the reading rule can use — or a FAILED parse. One definition of
 * usable (`usableStoredQualityWeights`), two consumers: `storedQualityWeightsSchema`
 * folds the failure into `null`, and the analysis-metadata field
 * (`qualityWeightsAtAnalysisSchema`) into "no field", because there an explicit
 * `null` already means something else ("equal weights, recorded as such") and a
 * spoilt value must not be folded into it. Unknown keys are dropped, as in the
 * reading rule. Not nullable itself: add `.nullable()` where a legal `null` exists.
 * (Its sibling `.strict()` objects mean "rejects unknown keys"; this one does not —
 * the name says only that an unusable value fails instead of becoming `null`.)
 */
export const storedQualityWeightsOrFailSchema = z
  .unknown()
  .transform((stored, ctx) => {
    const usable = usableStoredQualityWeights(stored);
    if (usable === null) {
      ctx.issues.push({
        code: 'custom',
        message: 'unusable stored quality weights',
        input: stored,
      });
      return z.NEVER;
    }
    return usable;
  })
  .pipe(storedQualityWeightsObjectSchema);

/**
 * Weights as READ back from storage. It never fails on a stored value: a value
 * the reading rule cannot use is `null` — "equal weights", exactly what every
 * score computation does with it (`canonicalizeQualityWeights`) — so one odd row
 * (`2.5`, `"3"`, an all-zero or hand-edited object) cannot fail a whole response.
 * A usable value keeps the current keys it carries (optional: the rest take the
 * default weight at the reader). Keys this code does not know — a stored JSON may
 * be newer than the code — are DROPPED, not passed through: the value is shown
 * and sent back through the strict write schema (`qualityWeightsSchema`), and a
 * seventh key echoed to the editor would make every save of an untouched form
 * fail. Dropping them cannot change a score either — no reader looks at them.
 *
 * Output: the object, or `null`. Use it in place of `.nullable()` on read paths.
 * Built on `storedQualityWeightsOrFailSchema`, so there is one definition of usable.
 */
export const storedQualityWeightsSchema = storedQualityWeightsOrFailSchema
  .nullable()
  .catch(null);

/**
 * Quality weights in the analysis metadata, WRITE form (the finalizer): an object,
 * `null` (equal weights, recorded explicitly) or absent — and a value the reading
 * rule cannot use is a parse ERROR ("unusable stored quality weights"). Recording
 * a spoilt value silently as "not recorded" would lose the weights of the analysis
 * with no trace, so the writer must fail loudly.
 */
export const qualityWeightsAtAnalysisWriteSchema = storedQualityWeightsOrFailSchema
  .nullable()
  .optional();

/**
 * The same field, READ form (replay, the review gate, the card): a spoilt value
 * becomes `undefined` — "not recorded" — never `null` ("scored with equal weights"
 * would be a claim nobody can vouch for), and never a failure of the whole
 * metadata (readers parse it strictly, and one spoilt field must not make the
 * other fields unreadable). `.catch(undefined)` leaves the key present with the
 * value `undefined`: test `=== undefined`, not `in`.
 */
export const qualityWeightsAtAnalysisSchema =
  qualityWeightsAtAnalysisWriteSchema.catch(undefined);

/**
 * PUT /api/v1/projects/:id/quality-weights body. `null` — reset to equal weights.
 * Six equal weights are stored as `null` too (spec D3): "equal" has one form.
 * `expectedWeights` — the weights the editor started from (what GET returned):
 * when the project's weights are anything else by now, someone saved in between
 * and the PUT answers 409 `QUALITY_WEIGHTS_CONFLICT` instead of overwriting a
 * save the editor never saw.
 */
export const updateQualityWeightsRequestSchema = z
  .object({
    weights: qualityWeightsSchema.nullable(),
    expectedWeights: qualityWeightsSchema.nullable(),
  })
  .strict();

/**
 * GET/PUT /api/v1/projects/:id/quality-weights response: the weights the score
 * reads (all six, after the reading rule), `null` — equal. The READ side, so a
 * stored value newer than this code (a seventh key) cannot fail the parse.
 */
export const qualityWeightsResponseSchema = z.object({
  weights: storedQualityWeightsSchema,
});

/** POST /api/v1/projects/:id/quality-weights/preview body — the draft to compare. */
export const qualityWeightsPreviewRequestSchema = z
  .object({ weights: qualityWeightsSchema.nullable() })
  .strict();

/**
 * What saving the draft would change on the project's calls of the last
 * `windowDays` days (spec §8): the counts and averages with the current
 * weights (`…Now`) and with the draft (`…New`).
 */
export const qualityWeightsPreviewResponseSchema = z.object({
  windowDays: z.number().int(),
  /** Calls that have a score now. */
  scoredCallsNow: z.number().int(),
  /**
   * Calls that would have a score with the draft. Not `now − losingScore`: a
   * draft can also GIVE a score back (a weight 0 → 1 on the only criterion a
   * call was rated on).
   */
  scoredCallsNew: z.number().int(),
  avgScoreNow: z.number().nullable(),
  avgScoreNew: z.number().nullable(),
  /** The example threshold the dialog names ("below 60"). */
  threshold: z.number(),
  belowThresholdNow: z.number().int(),
  belowThresholdNew: z.number().int(),
  /** Calls that have a score now and none with the draft (every rated criterion weighs 0). */
  losingScore: z.number().int(),
  /** Calls whose quality band (positive ≥ 80 / neutral ≥ 50 / negative / none) changes. */
  bandChanges: z.number().int(),
});

// ============================================================================
// Type exports (inferred from schemas)
// ============================================================================

export type QualityWeights = z.infer<typeof qualityWeightsSchema>;
/** What the read schema yields: the usable object, or `null` (equal weights). */
export type StoredQualityWeights = z.output<typeof storedQualityWeightsSchema>;
export type UpdateQualityWeightsRequest = z.infer<
  typeof updateQualityWeightsRequestSchema
>;
export type QualityWeightsResponse = z.infer<typeof qualityWeightsResponseSchema>;
export type QualityWeightsPreviewResponse = z.infer<
  typeof qualityWeightsPreviewResponseSchema
>;
export type ProjectStatus = z.infer<typeof projectStatusSchema>;
export type ProjectRole = z.infer<typeof projectRoleSchema>;
export type ChannelType = z.infer<typeof channelTypeSchema>;
export type IntegrationType = z.infer<typeof integrationTypeSchema>;
export type IntegrationVendor = z.infer<typeof integrationVendorSchema>;

export type ListProjectsQuery = z.infer<typeof listProjectsQuerySchema>;
export type CreateProjectRequest = z.infer<typeof createProjectRequestSchema>;
export type UpdateProjectRequest = z.infer<typeof updateProjectRequestSchema>;
export type CreateChannelRequest = z.infer<typeof createChannelRequestSchema>;
export type UpdateChannelRequest = z.infer<typeof updateChannelRequestSchema>;
export type CreateIntegrationRequest = z.infer<typeof createIntegrationRequestSchema>;
export type UpdateIntegrationRequest = z.infer<typeof updateIntegrationRequestSchema>;

export type ProjectResponse = z.infer<typeof projectResponseSchema>;
export type ProjectListItem = z.infer<typeof projectListItemSchema>;
export type ChannelResponse = z.infer<typeof channelResponseSchema>;
export type IntegrationResponse = z.infer<typeof integrationResponseSchema>;

export type AlertNotificationChannels = z.infer<typeof alertNotificationChannelsSchema>;
export type GetAlertNotificationChannelsResponse = z.infer<
  typeof getAlertNotificationChannelsResponseSchema
>;
export type TestAlertNotificationChannelResponse = z.infer<
  typeof testAlertNotificationChannelResponseSchema
>;

// ============================================================================
// Project Supervisors (Team tab)
// ============================================================================

/**
 * Who oversees a project.
 *
 * Only supervisors appear: client/admin are org-wide by role and never consult
 * `project_users`, and an agent is bounded by the record they handled, which
 * membership neither widens nor narrows. A supervisor's reach over transcripts
 * and analyses is the one thing these rows decide.
 */
export const projectSupervisorSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string(),
  /** Holds a `project_users` row for this project. */
  assigned: z.boolean(),
  /**
   * False when that row is a project-'admin' grant — the creator administering
   * what they made. This screen grants oversight and must not revoke that, so
   * the client renders such a row disabled rather than letting it be unticked
   * and saved with nothing happening.
   */
  manageable: z.boolean(),
});

export const listProjectSupervisorsResponseSchema = z.object({
  supervisors: z.array(projectSupervisorSchema),
});

/**
 * Desired end state, not a delta — the screen is a checklist.
 *
 * De-duplicated because `project_users` is uniquely indexed on
 * (project_id, user_id): a repeated id made the insert reject after the
 * removals in the same batch had already committed.
 */
export const updateProjectSupervisorsRequestSchema = z.object({
  userIds: z
    .array(z.string().uuid())
    .max(200)
    .transform((ids) => [...new Set(ids)]),
});

export const updateProjectSupervisorsResponseSchema = z.object({
  success: z.boolean(),
  assignedCount: z.number(),
});

export type ProjectSupervisor = z.infer<typeof projectSupervisorSchema>;
export type ListProjectSupervisorsResponse = z.infer<
  typeof listProjectSupervisorsResponseSchema
>;
export type UpdateProjectSupervisorsRequest = z.infer<
  typeof updateProjectSupervisorsRequestSchema
>;
export type UpdateProjectSupervisorsResponse = z.infer<
  typeof updateProjectSupervisorsResponseSchema
>;
